import { Add } from '@mui/icons-material';
import { Box, Menu, MenuItem, TextField, Typography } from '@mui/material';
import React, { KeyboardEventHandler, useCallback, useMemo, useRef, useState } from 'react';
import { t } from '../constants/translations';
import { useCafe } from '../contexts/cafe.context';
import { useLocale } from '../contexts/locale.context';
import Image from './Image';
import PrimaryButton from './PrimaryButton';

export type AddIngredientButtonProps = {
  options: string[];
  onAdd: (dishId: string) => void;
  small?: boolean;
};

const AddDishButton = ({ options, onAdd, small = false }: AddIngredientButtonProps) => {
  const { lang } = useLocale();
  const { dishesMap, categoriesMap } = useCafe();
  const ref = useRef<HTMLDivElement>();
  const [adding, setAdding] = useState(false);
  const [search, setSearch] = useState('');

  const handleClose = useCallback(() => {
    setAdding(false);
    setSearch('');
  }, []);

  const filteredOptions = useMemo(() => {
    const mapByCategory: Record<string, string[]> = {};
    options.forEach((o) => {
      if (!search || dishesMap[o]?.name.toLocaleUpperCase().includes(search.trim().toLocaleUpperCase())) {
        const key = dishesMap[o]?.categoryId;
        if (key) {
          mapByCategory[key] = [...(mapByCategory[key] || []), o];
        }
      }
    });
    return {
      mapByCategory,
    };
  }, [dishesMap, options, search]);

  const handlePressKey = useCallback<KeyboardEventHandler<HTMLDivElement>>((e) => {
    if (e.key.length === 1) {
      setSearch((prev) => prev + e.key);
    } else if (e.key === 'Backspace') {
      setSearch((prev) => prev.slice(0, prev.length - 1));
    }
  }, []);

  return (
    <Box ref={ref}>
      {!adding && (
        <PrimaryButton
          size={small ? 'small' : 'medium'}
          startIcon={<Add />}
          onClick={() => setAdding(true)}
          color="secondary"
        >
          {t.add_dish[lang]}
        </PrimaryButton>
      )}

      {adding && (
        <Box width={300}>
          <TextField
            color="secondary"
            value={search}
            autoFocus
            size="small"
            fullWidth
            onChange={(e) => setSearch(e.target.value)}
            label={t.select_component[lang]}
          />
          <Menu open onClose={handleClose} anchorEl={ref.current} onKeyDown={handlePressKey}>
            <Box sx={{ maxHeight: 300, width: 300, position: 'relative' }}>
              {Object.entries(filteredOptions.mapByCategory).map(([categoryId, options]) => (
                <Box key={categoryId} sx={{ mb: 1 }}>
                  <Typography variant="body2" color={(t) => t.palette.text.secondary} sx={{ ml: 1 }}>
                    {categoriesMap[categoryId]?.name}
                  </Typography>
                  {options.map((dishId) => (
                    <MenuItem
                      key={dishId}
                      sx={{ borderRadius: 1, p: 0.5 }}
                      onClick={() => {
                        onAdd(dishId);
                        handleClose();
                      }}
                    >
                      <Image src={dishesMap[dishId]?.imageUrl} />
                      <Typography sx={{ ml: 1 }}>{dishesMap[dishId]?.name}</Typography>
                    </MenuItem>
                  ))}
                </Box>
              ))}
            </Box>
          </Menu>
        </Box>
      )}
    </Box>
  );
};

export default AddDishButton;
